import React, { forwardRef, memo, Suspense } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import pascalcase from 'pascalcase';

// eslint-disable-next-line react/prop-types
const RefWrapper = forwardRef(({ children }, ref) => {
	if (ref) {
		return (
			<span ref={ref} data-only-ref='true'>
				{children}
			</span>
		);
	}
	return children;
});

const Icon = forwardRef(({ icon, className, color, size, forceFamily, ...props }, ref) => {
	const _icon = pascalcase(icon);

	const loadMaterial = () =>
		import(/* webpackChunkName: "icon-material-[request]" */ `./material-icons/${_icon}.js`);
	const loadBootstrap = () =>
		import(/* webpackChunkName: "icon-bootstrap-[request]" */ `./bootstrap/${_icon}.js`);
	const loadCustom = () =>
		import(/* webpackChunkName: "icon-custom-[request]" */ `./svg-icons/${_icon}.js`);

	const makeLoader = (order) => async () => {
		for (const step of order) {
			try {
				return await step();
			} catch (_) {
				// try next
			}
		}
		return { default: () => null }; // no crash if none exists
	};

	let order;
	let dataName = '';
	let familyClass = '';

	if (forceFamily === 'material') {
		order = [loadMaterial, loadBootstrap, loadCustom];
		dataName = `Material--${icon}`;
		familyClass = 'svg-icon--material';
	} else if (forceFamily === 'bootstrap') {
		order = [loadBootstrap, loadMaterial, loadCustom];
		dataName = `Bootstrap--${_icon}`;
		familyClass = 'svg-icon--bootstrap';
	} else if (forceFamily === 'custom') {
		order = [loadCustom, loadMaterial, loadBootstrap];
		dataName = `SvgIcon--${_icon}`;
		familyClass = 'svg-icon--custom';
	} else {
		// Default: Material -> Bootstrap -> Custom
		order = [loadMaterial, loadBootstrap, loadCustom];
		dataName = `Material--${icon}`;
		familyClass = 'svg-icon--material';
	}

	const LazyComponent = React.lazy(makeLoader(order));

	const _className = classNames(
		'svg-icon',
		{ [`svg-icon-${size}`]: size, [`text-${color}`]: color },
		className,
	);

	return (
		<RefWrapper ref={ref}>
			<Suspense fallback={null}>
				<LazyComponent data-name={dataName} className={classNames(familyClass, _className)} {...props} />
			</Suspense>
		</RefWrapper>
	);
});

Icon.propTypes = {
	icon: PropTypes.string.isRequired,
	className: PropTypes.string,
	color: PropTypes.oneOf([
		null,
		'primary',
		'secondary',
		'success',
		'info',
		'warning',
		'danger',
		'light',
		'dark',
	]),
	size: PropTypes.oneOf([
		null,
		'sm',
		'md',
		'lg',
		'2x',
		'3x',
		'4x',
		'5x',
		'6x',
		'7x',
		'8x',
		'9x',
		'10x',
	]),
	forceFamily: PropTypes.oneOf([null, 'custom', 'bootstrap', 'material']),
};
Icon.defaultProps = {
	className: null,
	color: null,
	size: null,
	forceFamily: null,
};

export default memo(Icon);
